/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {SensorUnits} from '../types/entities';
import {TemperatureUnit} from '../types/general';
import {isImperial} from '../units/helpers';
import {Unit} from '../units/units';
import {roundToPrecision} from './NumberUtil';

/**
 * Converts kPa units to PSI units.
 *
 * ```typescript
 *
 *   import {kpaToPsi} from '@ion/core/src/utils/';
 *
 *   const psi = kpaToPsi(10, 2) // 1.45
 *   const psi = kpaToPsi(10)    // 1
 *
 * ```
 * @param kpa       Pressure in kPa units.
 * @param precision Precision to round to.
 * @returns number
 */
export const kpaToPsi = (kpa: number, precision: number = 0): number => {
  return roundToPrecision(
    kpa * 0.145038,
    precision
  );
};

/**
 * Converts PSI units to kPa units.
 *
 * ```typescript
 *
 *   import {psiToKpa} from '@ion/core/src/utils/';
 *
 *   const kpa = psiToKpa(100, 3) // 689.476
 *   const kpa = psiToKpa(100)    // 689
 *
 * ```
 * @param psi       Pressure in PSI.
 * @param precision Precision to round to.
 * @returns number
 */
export const psiToKpa = (psi: number, precision: number = 0): number => {
  return roundToPrecision(
    psi * 6.89476,
    precision
  );
};

/**
 * Converts between kPa and PSI.
 * @param value The value to convert.
 * @param toUnit The unit to convert to: 'kPa' or 'psi'.
 * @returns The converted value.
 */
export const convertPressure = (value: number, toUnit: SensorUnits.KPA | SensorUnits.PSI): number => {
  if (toUnit === SensorUnits.PSI) {
    return value * 0.145038;
  } else if (toUnit === SensorUnits.KPA) {
    return value * 6.89476;
  } else {
    throw new Error('Invalid unit');
  }
};

/**
 * Converts degrees Celsius to degrees Fahrenheit.
 *
 * ```typescript
 *
 *   import {celsiusToFahrenheit} from '@ion/core/src/utils/';
 *
 *   const f = celsiusToFahrenheit(100, 3) // 212
 *   const f = celsiusToFahrenheit(100)    // 212
 *
 * ```
 * @param celsius   Temperature in degrees Celsius.
 * @param precision Precision to round to.
 * @returns number
 */
export const celsiusToFahrenheit = (celsius: number, precision: number = 0): number => {
  return roundToPrecision(
    (celsius * 1.8) + 32,
    precision
  );
};

/**
 * Converts degrees Fahrenheit to degrees Celsius.
 *
 * ```typescript
 *
 *   import {fahrenheitToCelsius} from '@ion/core/src/utils/';
 *
 *   const c = fahrenheitToCelsius(100, 3) // 37.778
 *   const c = fahrenheitToCelsius(100)    // 38
 *
 * ```
 * @param fahrenheit Temperature in degrees Fahrenheit.
 * @param precision  Precision to round to.
 * @returns number
 */
export const fahrenheitToCelsius = (fahrenheit: number, precision: number = 0): number => {
  return roundToPrecision(
    (fahrenheit - 32) / 1.8,
    precision
  );
};

/**
 *
 * genericUnitConversion converts a value from one unit to another.
 *
 * ```typescript
 *
 *   import {genericUnitConversion} from '@ion/core/src/utils/Conversions';
 *
 *   const  value = genericUnitConversion(value, type, preferredType);
 *
 * ```
 *
 * @param value
 * @param type
 * @param preferredType
 *  @param precision
 * @returns number
 */
// eslint-disable-next-line max-params
export const genericUnitConversion = (value, type, preferredType, precision = 0) => {
  if (isImperial(preferredType?.value)) {
    if (type === 'C') {
      return celsiusToFahrenheit(value, precision);
    }
    return value;
  } else {
    if (type === 'F') {
      return fahrenheitToCelsius(value, precision);
    }
    return value;
  }
};

/**
 *
 * getTemperatureUnit returns the unit of measure.
 *
 * ```typescript
 *
 *   import {getTemperatureUnit} from '@ion/core/src/utils/';
 *
 *   const  value = getTemperatureUnit(unit);
 *
 * ```
 *
 * @param unit
 * @returns TemperatureUnit
 */
export const getTemperatureUnit = (unit: Unit = Unit.US): TemperatureUnit => {
  if (isImperial(unit)) {
    return TemperatureUnit.Fahrenheit;
  }
  return TemperatureUnit.Celsius;
};

/**
 * Converts time hour to minutes
 *
 * ```typescript
 *
 *   import {covertHoursToMinutes} from '@ion/core/src/utils/';
 *
 *   const time = covertHoursToMinutes(1.46, 3) // 87.6
 *   const time = covertHoursToMinutes(1.5)    // 90
 *
 * ```
 */
export const covertHoursToMinutes = (time: number, precision: number = 0) => roundToPrecision(time * 60, precision);

/**
 * Converts time minutes to hour
 *
 * ```typescript
 *
 *   import {covertHoursToMinutes} from '@ion/core/src/utils/';
 *
 *   const time = covertMinutesToHours(90, 2) // 1.5
 *   const time = covertMinutesToHours(90)    // 1
 *
 * ```
 */
export const covertMinutesToHours = (time: number, precision: number = 0) => roundToPrecision(time / 60, precision);
